import { Button, Modal, Space } from 'antd';
import { IModal } from 'common/models/model.interface';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import fastAccessService from 'common/services/fast-access-service/fast-access-service';
import QRCodeAdmitGuestModal from './eiq-secure-qr-code-admit-guest-modal';
import QRCodeAdmitResidentModal from './eiq-secure-qr-code-admit-resident-modal';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import { observer } from 'mobx-react-lite';
import { IQRCodeAdmitGuestModalProps, IQRCodeAdmitResidentModalProps } from './eiq-secure-qr-code-admit-modal.types';
import userAuthStore from 'common/stores/user-auth/user-auth.store';
import { EntryType } from 'common/enums/entry-type.enum';
import guestService from 'common/services/guest-service/guest.service';
import { notification } from 'common/utils/notification';
import { IAdmitResident } from 'common/services/guest-service/guest.service.types';
import './eiq-secure-qr-code-scanner.scss';
import QRCodeScanDetector from '../eiq-secure-qr-code-scan-detector/eiq-secure-qr-code-scan-detector';
import { getCode } from 'common/helpers/eiq-passport.helper';
import { isServiceGuest } from 'common/helpers/guest-type.helpers';
import { IFastAccessPassRead } from 'common/services/fast-access-service/fast-access-service.types';
import featureStore from 'common/stores/features/feature.store';
import { IGuestRead } from 'common/services/residents-service/residents.service.types';
import plateRecognitionContext from 'features/eiq-secure/stores/license-plate-recognition-context';
import successSound from 'assets/sounds/success.wav';
import errorSound from 'assets/sounds/error.wav';
import { trackPromise } from 'react-promise-tracker';
import citationService from 'common/services/citation-service/citation.service';
import DrivingPrivilegesRevokeModal from '../driving-privileges-revoked-modal/driving-privileges-revoked-modal';
import TurnbackModal from '../turnback-modal/turnback-modal';

const QRcodeReaderId = 'eiq-fast-access-reader-id';

const QRCodeScannerModal: FC<IModal<any, any>> = observer(({ isOpen, onClose }) => {
  const admitGuestModal = useModal();
  const admitResidentModal = useModal();
  const scanner = useRef<any>(null);
  const [admitGuestData, setAdmitGuestData] = useState<any>();

  const turnbackModal = useModal({
    onClose: (isSuccessSaved, turnbackReason) => {
      if (isSuccessSaved) {
        trackPromise(turnBackGuest(turnbackReason));
      }
    },
  });

  const drivingPrivilagesRevokeModal = useModal({
    onClose: (isSuccess, type) => {
      if (isSuccess) {
        if (type == 'admit') {
          trackPromise(admitGuest());
        }
        if (type == 'turnback') {
          turnbackModal.showModal();
        }
      }
    },
  });

  const admitGuest = async () => {
    if (!admitGuestData) {
      return;
    }

    const { residentId, addressId, guestId } = admitGuestData;
    await guestService.admitGuest(residentId, addressId, guestId, admitGuestData);
    notification.success({ message: 'Guest has been admitted' });
    plateRecognitionContext.clear();
    playSound(successSound);
  };

  const turnBackGuest = async (turnbackReason: string) => {
    if (!admitGuestData) {
      return;
    }

    const { residentId, addressId, guestId } = admitGuestData;
    const data = { ...admitGuestData, reason: turnbackReason };
    await guestService.turnbackGuest(residentId, addressId, guestId, data);
    notification.success({ message: 'The guest has been turned back' });
    plateRecognitionContext.clear();
    playSound(successSound);
  };

  const handleOnClose = () => {
    if (scanner.current) {
      if (scanner.current.isScanning) {
        scanner.current.stop().then(() => scanner.current.clear());
      } else {
        scanner.current.clear();
      }
    }
    onClose();
  };

  const hasAlert = (data: IFastAccessPassRead) => {
    const { residentAlertMessage, addressAlertMessage } = data;
    return featureStore.isFastAccessAlertsEnabled && (residentAlertMessage || addressAlertMessage);
  };

  const hasRestriction = (guest: IGuestRead | null) => {
    return featureStore.isFastAccessRestrictionsEnabled && guest?.restrictions;
  };

  const playSound = (sound: any) => {
    const audio = new Audio(sound);
    audio.play();
  };

  const getDecodedTextHandle = async (text: string) => {
    try {
      const eiqPassport: IFastAccessPassRead | null = await fastAccessService.getFastAccessPass(text);

      if (!eiqPassport) {
        notification.error({ message: 'Some error occurred' });
        return;
      }

      const {
        addressId,
        residentId,
        guest,
        carExpirationDate,
        carNumber,
        carState,
        deviceId,
        residentAlertMessage,
        addressAlertMessage,
        address,
        resident,
      } = eiqPassport;
      const gate = userAuthStore.defaultGate;

      const isShowAadmiGuestPopUp = guest && (featureStore.isDriveIdEnabled || featureStore.isPlateScanEnabled);

      const isShowModal =
        !carNumber ||
        !carState ||
        !gate ||
        isServiceGuest(guest?.type) ||
        hasAlert(eiqPassport) ||
        hasRestriction(guest) ||
        isShowAadmiGuestPopUp;

      if (isShowModal) {
        if (guest) {
          const admitGuestInitData: IQRCodeAdmitGuestModalProps = {
            addressId: addressId,
            address: address,
            residentId: residentId,
            resident: resident,
            guest: guest,
            gate: userAuthStore.defaultGate,
            carLicensePlate: carNumber,
            carLicenseState: carState,
            isEIQPassportUsed: true,
            residentAlertMessage: residentAlertMessage,
            addressAlertMessage: addressAlertMessage,
          };

          admitGuestModal.setModalInitData(admitGuestInitData);
          admitGuestModal.showModal();
        } else {
          const admitResidentInitData: IQRCodeAdmitResidentModalProps = {
            addressId: addressId,
            address: address,
            residentId: residentId,
            resident: resident,
            gate: userAuthStore.defaultGate,
            carExpirationDate: carExpirationDate,
            carLicensePlate: carNumber,
            carLicenseState: carState,
            deviceId: deviceId,
            residentAlertMessage: residentAlertMessage,
            addressAlertMessage: addressAlertMessage,
            isEIQPassportUsed: true,
          };

          admitResidentModal.setModalInitData(admitResidentInitData);
          admitResidentModal.showModal();
        }
      } else {
        const admitData: IAdmitResident = {
          gate: gate,
          entryType: EntryType.Driver,
          carState: carState,
          carLicensePlate: carNumber,
          carExpirationDate: carExpirationDate,
          deviceId: deviceId,
          isEIQPassportUsed: true,
        };

        if (guest && featureStore.isCitationsEnabled) {
          const result = await trackPromise(citationService.isLicensePlateDrivingPrivilegesRevoked(carNumber));

          if (result.isDrivingPrivilegesRevoked) {
            setAdmitGuestData({
              ...admitData,
              residentId,
              addressId,
              guestId: guest.id,
              isDrivingPrivilagesRevoked: result.isDrivingPrivilegesRevoked,
            });

            const initData = {
              drivingPrivilageData: result,
              isVacantAddress: false,
            };

            drivingPrivilagesRevokeModal.setModalInitData(initData);
            drivingPrivilagesRevokeModal.showModal();
            return;
          }
        }

        if (guest) {
          await guestService.admitGuest(residentId, addressId, guest.id, admitData);
          notification.success({ message: 'Guest has been admitted' });
          plateRecognitionContext.clear();
          playSound(successSound);
        } else {
          await guestService.admitResident(residentId, addressId, admitData);
          notification.success({ message: 'The resident  has been admitted' });
          plateRecognitionContext.clear();
          playSound(successSound);
        }
      }
    } catch (error: any) {
      playSound(errorSound);
      if (error.response?.status === 404) {
        notification.error({ message: error.response.data.message, duration: 0 });
      }
    }
  };

  const getScanner = (): Html5Qrcode => {
    if (scanner.current) {
      return scanner.current;
    }
    const result = new Html5Qrcode(QRcodeReaderId);
    scanner.current = result;

    return result;
  };

  useEffect(() => {
    if (isOpen) {
      const html5QrCode = getScanner();

      html5QrCode
        .start(
          { facingMode: 'environment' },
          {
            fps: 5,
            qrbox: { width: 250, height: 250 },
          },
          (decodedText, decodedResult) => {
            if (decodedText) {
              getDecodedTextHandle(getCode(decodedText));
            }
            handleOnClose();
          },
          (error) => {
            console.log(error);
          },
        )
        .catch((error) => {
          console.log(error);
        });
    }
  }, [isOpen]);

  const onScanDetected = (code: string) => {
    const result = code.trim();
    getDecodedTextHandle(result);
  };

  return (
    <>
      <Modal centered width={400} open={isOpen} wrapClassName="custom-modal custom-modal--so" closable={false} title="EIQ Passport scanner">
        <div className="qr-code-scanner" id={QRcodeReaderId} />
        <Space className="footer">
          <Button className="eiq-button" type="link" onClick={handleOnClose}>
            Cancel
          </Button>
        </Space>
      </Modal>
      {admitGuestModal.isOpenModal && (
        <QRCodeAdmitGuestModal
          isOpen={admitGuestModal.isOpenModal}
          onClose={admitGuestModal.hideModal}
          initData={admitGuestModal.initData}
        />
      )}
      {admitResidentModal.isOpenModal && (
        <QRCodeAdmitResidentModal
          isOpen={admitResidentModal.isOpenModal}
          onClose={admitResidentModal.hideModal}
          initData={admitResidentModal.initData}
        />
      )}
      {drivingPrivilagesRevokeModal.isOpenModal && (
        <DrivingPrivilegesRevokeModal
          isOpen={drivingPrivilagesRevokeModal?.isOpenModal}
          onClose={drivingPrivilagesRevokeModal?.hideModal}
          initData={drivingPrivilagesRevokeModal?.initData}
        />
      )}
      {turnbackModal.isOpenModal && (
        <TurnbackModal title="Turnback" isOpen={turnbackModal?.isOpenModal} onClose={turnbackModal?.hideModal} />
      )}
      <QRCodeScanDetector onScan={onScanDetected} />
    </>
  );
});

export default React.memo(QRCodeScannerModal, (prevProps, nextProps) => {
  return prevProps.isOpen === nextProps.isOpen;
});
